import http from '../../services/http';

const tailwindWidths = {
  10: 'w-[10%]',
  20: 'w-[20%]',
  30: 'w-[30%]',
  40: 'w-[40%]',
  50: 'w-1/2',
  60: 'w-[60%]',
  70: 'w-[70%]',
  80: 'w-[80%]',
  90: 'w-[90%]',
  100: 'w-[100%]',
};

const getGroupStructure = (array) => {
  return array.reduce((acc, item) => {
    acc[item] = {
      totals: 0,
      states: [],
      percent: 0,
    };
    return acc;
  }, {});
};

/** Group states by
 * weeks: 1-3 weeks (1-3), 2-5 weeks (2-5), 3-6 weeks (3-6)
 * Business Days: 1 day (1), 2 days (2), 3 days (3)
 * Price: $50 (50), $70 (70) , $75 (75)
 */
const groupedStates = {
  filingTimes: getGroupStructure(['1_3', '2_5', '3_6']),
  expeditedTimes: getGroupStructure([1, 2, 3]),
  expeditedPrices: getGroupStructure([50, 70, 75]),
};

export default {
  init(instance) {
    document.addEventListener('alpine:init', () => {
      instance.data('CorporateFilingTimes', () => {
        const getTailwindWidth = ({ total, value }) => {
          const percent = Math.round((value / total) * 100);
          const roundedWidth = Math.round(percent / 10) * 10;
          if (roundedWidth === 0) return tailwindWidths[10];
          return tailwindWidths[roundedWidth] || tailwindWidths[100];
        };

        const splitNumbers = (str) => {
          return parseInt(str.split(' ')[0]);
        };

        const groupByWeeks = (states) => {
          const filteredWeeks = states.filter((state) => {
            const weeks = splitNumbers(state.normalProcessingTime);
            if (weeks <= 6) return weeks;
            return false;
          });
          const group = filteredWeeks.reduce((acc, state) => {
            const weeks = splitNumbers(state.normalProcessingTime);
            const weekCategory =
              weeks <= 3 ? '1_3' : weeks <= 5 ? '2_5' : '3_6';

            if (!acc[weekCategory]) {
              acc = getGroupStructure(['1_3', '2_5', '3_6']);
            }

            acc[weekCategory].totals++;
            acc[weekCategory].states.push(state);
            acc[weekCategory].percent = getTailwindWidth({
              total: filteredWeeks.length,
              value: acc[weekCategory].totals,
            });

            return acc;
          }, {});

          return group;
        };

        const groupByDays = (states) => {
          const filteredDays = states.filter((state) => {
            const days = splitNumbers(state.expeditedProcessingTime);
            if (days <= 3) return days;
            return false;
          });
          const group = filteredDays.reduce((acc, state) => {
            const days = splitNumbers(state.expeditedProcessingTime);

            if (!acc[days]) {
              acc[days] = {
                totals: 0,
                states: [],
                percent: 0,
              };
            }

            acc[days].totals++;
            acc[days].states.push(state);
            acc[days].percent = getTailwindWidth({
              total: filteredDays.length,
              value: acc[days].totals,
            });

            return acc;
          }, {});

          return group;
        };

        const groupedByPrice = (states) => {
          const filteredPrice = states.filter((state) => {
            const priceCategories = [50, 70, 75];
            if (priceCategories.includes(state.expeditedPrice)) return state;
            return false;
          });
          const group = filteredPrice.reduce((acc, state) => {
            if (!acc[state.expeditedPrice]) {
              acc[state.expeditedPrice] = {
                totals: 1,
                states: [state],
                percent: getTailwindWidth({
                  total: filteredPrice.length,
                  value: 1,
                }),
              };
            } else {
              acc[state.expeditedPrice].totals++;
              acc[state.expeditedPrice].states.push(state);
              acc[state.expeditedPrice].percent = getTailwindWidth({
                total: filteredPrice.length,
                value: acc[state.expeditedPrice].totals,
              });
            }

            return acc;
          }, {});

          return group;
        };

        return {
          states: [],
          selectedStates: [],
          filteredStates: [],
          inputValue: '',
          groupedStates,

          async fetchStates() {
            const apiUrl = import.meta.env.VITE_GET_STATE_FILING_TIMES;
            try {
              const response = await http.get(apiUrl, { headers: null });

              if (response?.status === 200) {
                this.states = response.data;
                // Group states by weeks, days and price
                this.groupedStates.filingTime = groupByWeeks(response.data);
                this.groupedStates.expeditedTime = groupByDays(response.data);
                this.groupedStates.expeditedPrice = groupedByPrice(
                  response.data,
                );
              }
            } catch (error) {
              console.error('Error fetching states:', error);
            }
          },

          selectAll() {
            this.selectedStates = [...this.states];
          },
          clearSelection() {
            this.selectedStates = [];
          },

          filterStates(query) {
            this.inputValue = query;
            const queriedStates = [
              ...this.states.filter((s) =>
                s.state.toLowerCase().includes(query.toLowerCase()),
              ),
            ];

            if (!this.selectedStates.length) {
              this.filteredStates = queriedStates;
              return;
            }

            this.filteredStates = queriedStates.filter((s) => {
              return !this.selectedStates.find(
                (selected) => selected.state === s.state,
              );
            });
          },
          resetFilter() {
            this.filteredStates = [];
          },
          addState(stateName) {
            if (this.selectedStates.find((s) => s.state === stateName)) {
              return;
            }
            const state = this.states.find((s) => s.state === stateName);
            this.selectedStates.push(state);
            this.inputValue = '';
            this.filteredStates = [];
          },

          removeState(stateName) {
            this.selectedStates = this.selectedStates.filter(
              (s) => s.state !== stateName,
            );
          },
        };
      });
    });
  },
};
